// Shared bits for the staff money pages. Dates come from kit/time, so every page reads them one way.
import type { ReactNode } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api } from '../../lib'
import { when as kitWhen, type When } from './kit/time'

export function useStaffAction<B = unknown>(path: (id: string) => string, invalidate: string[][]) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body?: B }) => api<Record<string, unknown>>('POST', path(id), body),
    onSuccess: () => invalidate.forEach((k) => qc.invalidateQueries({ queryKey: k })),
  })
}

/** "Nov 2, 3:14 PM", or empty when there is no time. */
export function when(iso?: When): string {
  return iso ? kitWhen(iso) : ''
}

/** Whole days between two times, as words: "today", "1 day", "12 days". Null when either is missing. */
export function daysWords(from: When, to: When): string | null {
  if (!from || !to) return null
  const a = new Date(String(from)).getTime()
  const b = new Date(String(to)).getTime()
  if (Number.isNaN(a) || Number.isNaN(b)) return null
  const d = Math.floor((b - a) / 86_400_000)
  return d <= 0 ? 'today' : d === 1 ? '1 day' : `${d} days`
}

/** Label and value pairs, one line each. A pair with no value shows a dash. */
export function Facts({ items, testid }: { items: [ReactNode, ReactNode][]; testid?: string }) {
  return (
    <dl className="st-facts" data-testid={testid}>
      {items.map(([k, v], i) => [
        <dt key={`k${i}`}>{k}</dt>,
        <dd key={`v${i}`}>{v === null || v === undefined || v === '' ? '–' : v}</dd>,
      ])}
    </dl>
  )
}

export const CompanyLink = ({ id, name }: { id: string; name: ReactNode }) => <Link className="st-link" to={`/staff/company/${id}`}>{name}</Link>
export const PartnerLink = ({ id, name }: { id: string; name: ReactNode }) => <Link className="st-link" to={`/staff/partner/${id}`}>{name}</Link>

export { parseDollars } from './money'
